import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AddressApi, AddressApiError } from '../../lib/addressApi';
import { DeliveryAddress, DeliveryAddressInput } from '../../types/api';
import { useAuthStore } from '../../store/authStore';
import AddressLines from './AddressLines';
import DeliveryAddressForm, { emptyAddress, toAddressInput } from './DeliveryAddressForm';

type Editor =
  | { readonly kind: 'closed' }
  | { readonly kind: 'create' }
  | { readonly kind: 'edit'; readonly id: string };
interface Props {
  readonly api: AddressApi;
}
export default function DeliveryAddresses({ api }: Props) {
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState<readonly DeliveryAddress[]>([]);
  const [editor, setEditor] = useState<Editor>({ kind: 'closed' });
  const [draft, setDraft] = useState<DeliveryAddressInput>(emptyAddress);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  // Blocks a second submit fired in the same tick, before `busy` disables the buttons.
  const saving = useRef(false);
  const generation = useRef(0);
  const mounted = useRef(true);
  const handleError = useCallback(
    (error: unknown) => {
      if (error instanceof AddressApiError && error.status === 401) {
        useAuthStore.setState({ user: null, status: 'guest' });
        navigate('/login', { replace: true });
      } else setError(error instanceof Error ? error.message : 'Could not load addresses.');
    },
    [navigate],
  );
  const load = useCallback(
    async (signal?: AbortSignal) => {
      const request = ++generation.current;
      const isCurrent = () => mounted.current && request === generation.current && !signal?.aborted;
      setLoading(true);
      try {
        const result = await api.list(signal);
        if (isCurrent()) setAddresses(result);
      } catch (error: unknown) {
        if (isCurrent()) handleError(error);
      } finally {
        if (isCurrent()) setLoading(false);
      }
    },
    [api, handleError],
  );
  useEffect(() => {
    mounted.current = true;
    const controller = new AbortController();
    void load(controller.signal);
    return () => {
      mounted.current = false;
      controller.abort();
    };
  }, [load]);
  const mutate = async (operation: () => Promise<unknown>, close: boolean) => {
    if (saving.current) return;
    saving.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await operation();
      if (!mounted.current) return;
      if (close) {
        setEditor({ kind: 'closed' });
        setDeleting(null);
      }
      setNotice('Delivery address updated.');
      await load();
    } catch (error: unknown) {
      if (mounted.current) handleError(error);
    } finally {
      saving.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (editor.kind === 'closed') return;
    const input = { ...draft, address_line_2: draft.address_line_2?.trim() || null };
    const current = editor;
    void mutate(() => (current.kind === 'edit' ? api.replace(current.id, input) : api.create(input)), true);
  };
  return (
    <div>
      {error && <p role="alert">{error}</p>}
      {notice && <p role="status">{notice}</p>}
      {loading ? (
        <p role="status">Loading addresses…</p>
      ) : addresses.length === 0 ? (
        <p>No delivery addresses saved.</p>
      ) : (
        addresses.map((address) => (
          <article className="address-card" key={address.id}>
            <strong>{address.recipient_name}</strong>
            {address.is_default && <span> · Default</span>}
            <p>
              <AddressLines address={address} />
            </p>
            <div className="address-actions">
              <button
                disabled={busy}
                onClick={() => {
                  setDraft(toAddressInput(address));
                  setEditor({ kind: 'edit', id: address.id });
                  setNotice('');
                }}
              >
                Edit address
              </button>
              <button disabled={busy} onClick={() => setDeleting(address.id)}>
                Delete address
              </button>
              {!address.is_default && (
                <button disabled={busy} onClick={() => void mutate(() => api.setDefault(address.id), false)}>
                  Set as default
                </button>
              )}
            </div>
          </article>
        ))
      )}
      {editor.kind === 'closed' ? (
        <button
          disabled={busy}
          onClick={() => {
            setDraft(emptyAddress);
            setEditor({ kind: 'create' });
          }}
        >
          Add delivery address
        </button>
      ) : (
        <form onSubmit={submit}>
          <DeliveryAddressForm value={draft} disabled={busy} onChange={setDraft} />
          <div className="address-actions">
            <button disabled={busy} type="submit">
              {busy ? 'Saving…' : 'Save address'}
            </button>
            <button disabled={busy} type="button" onClick={() => setEditor({ kind: 'closed' })}>
              Cancel
            </button>
          </div>
        </form>
      )}
      {deleting && (
        <div role="dialog" aria-label="Delete delivery address">
          <p>Delete this delivery address?</p>
          <button disabled={busy} onClick={() => void mutate(() => api.remove(deleting), true)}>
            Confirm delete
          </button>
          <button disabled={busy} onClick={() => setDeleting(null)}>
            Cancel deletion
          </button>
        </div>
      )}
      {error && (
        <button
          disabled={busy || loading}
          onClick={() => {
            setError('');
            void load();
          }}
        >
          Retry loading addresses
        </button>
      )}
    </div>
  );
}
